// Lightweight i18n: a flat dictionary per language, no runtime i18n library
// (KISS). The active language is derived from the route prefix (useLang).

export type Lang = 'de' | 'en';

export interface Strings {
  langName: string;
  switchTo: string;
  nav: { privacy: string; imprint: string; passwords: string; sshKeys: string; tools: string };
  menu: { security: string; dev: string; images: string };
  hero: { title: string; subtitle: string; localBadge: string };
  cards: {
    pwdgenTitle: string;
    pwdgenDesc: string;
    sshgenTitle: string;
    sshgenDesc: string;
    uuidgenTitle: string;
    uuidgenDesc: string;
    hashgenTitle: string;
    hashgenDesc: string;
    qrgenTitle: string;
    qrgenDesc: string;
    pgpgenTitle: string;
    pgpgenDesc: string;
    favicongenTitle: string;
    favicongenDesc: string;
    imgconvTitle: string;
    imgconvDesc: string;
    open: string;
  };
  trust: { title: string; body: string };
  sections: { popular: string; more: string };
  seo: {
    aboutTitle: string;
    aboutBody: string;
    faqTitle: string;
    faq: { q: string; a: string }[];
  };
  footer: { rights: string; noStorage: string };
}

const de: Strings = {
  langName: 'Deutsch',
  switchTo: 'English',
  nav: { privacy: 'Datenschutz', imprint: 'Impressum', passwords: 'Passwörter', sshKeys: 'SSH-Keys', tools: 'Tools' },
  menu: { security: 'Sicherheit', dev: 'Entwickler-Tools', images: 'Bilder' },
  hero: {
    title: 'Sichere Tools, komplett im Browser',
    subtitle:
      'Erzeuge Passwörter und SSH-Schlüssel direkt in deinem Browser. Nichts wird an einen Server gesendet oder gespeichert.',
    localBadge: '100 % lokal · kostenlos',
  },
  cards: {
    pwdgenTitle: 'Passwort-Generator',
    pwdgenDesc:
      'Starke Zufallspasswörter, Passphrasen und merkbare Kombinationen — mit Live-Stärkeanzeige.',
    sshgenTitle: 'SSH-Key-Generator',
    sshgenDesc:
      'Ed25519- und RSA-4096-Schlüsselpaare im OpenSSH-Format, sofort einsatzbereit.',
    uuidgenTitle: 'UUID-Generator',
    uuidgenDesc: 'UUIDs der Version 4 und 7 — einzeln oder als Liste, mit Copy.',
    hashgenTitle: 'Hash-Generator',
    hashgenDesc: 'SHA-1/256/384/512 für Text und Dateien, vollständig lokal.',
    qrgenTitle: 'QR-Code-Generator',
    qrgenDesc: 'QR-Codes aus Text oder URL, Download als PNG oder SVG.',
    pgpgenTitle: 'PGP-Key-Generator',
    pgpgenDesc: 'OpenPGP-Ed25519-Keypaar, privater Schlüssel passphrasen-verschlüsselt.',
    favicongenTitle: 'Favicon-Generator',
    favicongenDesc: 'Aus einem Bild ein komplettes Favicon-Set (ICO, PNGs, Manifest) als ZIP.',
    imgconvTitle: 'Bildformat-Converter',
    imgconvDesc: 'Bilder zwischen PNG, JPEG und WebP umwandeln und verkleinern.',
    open: 'Öffnen',
  },
  trust: {
    title: 'Deine Daten bleiben bei dir',
    body:
      'Alles läuft lokal über die Web Crypto API deines Browsers. Es gibt keine serverseitige Verarbeitung und keine Speicherung deiner Passwörter oder Schlüssel.',
  },
  sections: { popular: 'Beliebt', more: 'Weitere Tools' },
  seo: {
    aboutTitle: 'Warum ecosquirrel-Tools?',
    aboutBody:
      'Unsere Werkzeuge erzeugen sensible Daten wie Passwörter und SSH-Schlüssel ausschließlich lokal in deinem Browser. Es gibt keine serverseitige Verarbeitung und keine Speicherung. Schließe einfach den Tab, und nichts bleibt zurück.',
    faqTitle: 'Häufige Fragen',
    faq: [
      {
        q: 'Werden meine Passwörter oder Schlüssel gespeichert?',
        a: 'Nein. Alles läuft lokal im Browser über die kryptografisch sichere Web Crypto API. Es werden keine Daten an einen Server gesendet.',
      },
      {
        q: 'Sind die Tools kostenlos?',
        a: 'Ja. Die Tools sind kostenlos und werden über Werbung finanziert.',
      },
      {
        q: 'Welche Werkzeuge gibt es?',
        a: 'Oben siehst du die beliebtesten Tools als Kacheln. Alle weiteren findest du im Menü oben unter „Sicherheit" (Passwörter, SSH-Keys, PGP) und „Entwickler-Tools" (UUID, Hash, QR-Code, Favicon, Bild-Converter).',
      },
    ],
  },
  footer: {
    rights: 'Kostenlose Tools von ecosquirrel.',
    noStorage: 'Keine serverseitige Speicherung. Keine Übertragung deiner Daten.',
  },
};

const en: Strings = {
  langName: 'English',
  switchTo: 'Deutsch',
  nav: { privacy: 'Privacy', imprint: 'Imprint', passwords: 'Passwords', sshKeys: 'SSH keys', tools: 'Tools' },
  menu: { security: 'Security', dev: 'Developer tools', images: 'Images' },
  hero: {
    title: 'Secure tools, entirely in your browser',
    subtitle:
      'Generate passwords and SSH keys right in your browser. Nothing is sent to a server or stored.',
    localBadge: '100% local · free',
  },
  cards: {
    pwdgenTitle: 'Password Generator',
    pwdgenDesc:
      'Strong random passwords, passphrases and memorable combinations — with a live strength meter.',
    sshgenTitle: 'SSH Key Generator',
    sshgenDesc: 'Ed25519 and RSA-4096 key pairs in OpenSSH format, ready to use.',
    uuidgenTitle: 'UUID Generator',
    uuidgenDesc: 'UUID version 4 and 7 — one or many at once, with copy.',
    hashgenTitle: 'Hash Generator',
    hashgenDesc: 'SHA-1/256/384/512 for text and files, fully local.',
    qrgenTitle: 'QR Code Generator',
    qrgenDesc: 'QR codes from text or URL, download as PNG or SVG.',
    pgpgenTitle: 'PGP Key Generator',
    pgpgenDesc: 'OpenPGP Ed25519 key pair, private key passphrase-encrypted.',
    favicongenTitle: 'Favicon Generator',
    favicongenDesc: 'Turn one image into a full favicon set (ICO, PNGs, manifest) as a ZIP.',
    imgconvTitle: 'Image Format Converter',
    imgconvDesc: 'Convert images between PNG, JPEG and WebP and resize them.',
    open: 'Open',
  },
  trust: {
    title: 'Your data stays with you',
    body:
      "Everything runs locally via your browser's Web Crypto API. There is no server-side processing and no storage of your passwords or keys.",
  },
  sections: { popular: 'Popular', more: 'More tools' },
  seo: {
    aboutTitle: 'Why ecosquirrel tools?',
    aboutBody:
      'Our tools generate sensitive data such as passwords and SSH keys exclusively in your browser. There is no server-side processing and no storage. Just close the tab and nothing remains.',
    faqTitle: 'Frequently asked questions',
    faq: [
      {
        q: 'Are my passwords or keys stored?',
        a: 'No. Everything runs locally in the browser via the cryptographically secure Web Crypto API. No data is sent to a server.',
      },
      {
        q: 'Are the tools free?',
        a: 'Yes. The tools are free and funded through advertising.',
      },
      {
        q: 'Which tools are available?',
        a: 'The most popular tools are shown as tiles above. You will find all the others in the menu at the top under "Security" (passwords, SSH keys, PGP) and "Developer tools" (UUID, hash, QR code, favicon, image converter).',
      },
    ],
  },
  footer: {
    rights: 'Free tools by ecosquirrel.',
    noStorage: 'No server-side storage. Your data is never transmitted.',
  },
};

export const STRINGS: Record<Lang, Strings> = { de, en };
